import React, { useCallback, useMemo } from 'react';

import { InfoHint } from 'components/common/InfoHint';
import { useFormatMode } from 'state/FormatContext';
import type { ActionInputField } from 'model/actions';
import { formatTypeLabel } from 'model/actions';
import type { InputBinding } from 'model/workflow';
import { formatName } from 'model/nameFormatting';
import { COMMON_PROCESSING_JOB_INPUTS, getFieldHint, getTypeHint } from 'model/hints';
import { useInputBinding, type BindingKind } from 'hooks/useInputBinding';
import { toStringValue } from 'utils/strings';

import { ExpressionHelp } from './ExpressionHelp';
import Styled from './InputFieldRenderer.styled';

const {
  StyledRow,
  StyledRemoveButton,
  StyledHeader,
  StyledLabel,
  StyledTypeBadge,
  StyledRequiredMark,
  StyledRequiredWarning,
  StyledFullWidthInput,
  StyledExpressionWrapper,
  StyledExpressionTextarea,
  StyledStepOutputColumn,
  StyledSelect,
} = Styled;

const ALL_BINDING_KINDS: BindingKind[] = [
  'Constant',
  'StepOutput',
  'ProcessingJobInput',
  'Expression',
];

interface OwnProps {
  field: ActionInputField;
  binding: InputBinding | undefined;
  availableStepIds: string[];
  /** Look up the output names contributed by a given step id. */
  resolveStepOutputs: (stepId: string) => string[];
  /**
   * Stable across renders. Receives the field name + new binding so the
   * parent can keep a single `useCallback`-wrapped handler regardless of
   * how many fields are rendered. The renderer wraps this in a locally-
   * stable closure (see `handleBindingChange` below) so `React.memo` on
   * this component is actually effective.
   */
  onBindingChange: (fieldName: string, next: InputBinding) => void;
  /**
   * Optional. When provided, the renderer shows a remove button and
   * invokes this with the field name. Used for "extra" (non-contract)
   * inputs.
   */
  onRemove?: (fieldName: string) => void;
}

const bindingIsEmpty = (binding: InputBinding | undefined): boolean => {
  if (!binding) return true;
  if (binding.kind === 'Constant') {
    return binding.value === '' || binding.value === null || binding.value === undefined;
  }
  if (binding.kind === 'ProcessingJobInput') return !binding.target;
  if (binding.kind === 'StepOutput') return !binding.stepId || !binding.target;
  if (binding.kind === 'Expression') return !binding.expression;
  return false;
};

const InputFieldRenderer = ({
  field,
  binding,
  availableStepIds,
  resolveStepOutputs,
  onBindingChange,
  onRemove,
}: OwnProps) => {
  const nameMode = useFormatMode();

  // Wrap the parent's stable `onBindingChange` in a locally-stable closure
  // that bakes in `field.name`. The parent passes ONE `useCallback`-wrapped
  // handler for every field — without this wrapper we'd allocate a fresh
  // closure per field per render, defeating `React.memo` on this component.
  const handleBindingChange = useCallback(
    (next: InputBinding) => onBindingChange(field.name, next),
    [onBindingChange, field.name],
  );

  const handleRemove = useCallback(() => onRemove?.(field.name), [onRemove, field.name]);

  const {
    kind,
    setKind,
    setStepOutputRef,
    setProcessingJobInputTarget,
    setExpression,
    setConstant,
  } = useInputBinding(binding, handleBindingChange);

  const allowedSources = useMemo<BindingKind[]>(() => {
    const declared =
      field.allowedSources && field.allowedSources.length > 0
        ? [...(field.allowedSources as BindingKind[]), 'Expression']
        : ['Constant', 'Expression'];
    return ALL_BINDING_KINDS.filter((k) => declared.includes(k));
  }, [field.allowedSources]);

  // The effective kind for rendering: if the binding doesn't have one yet,
  // fall back to the first allowed source so the UI shows valid controls.
  const effectiveKind: BindingKind = binding?.kind ?? allowedSources[0] ?? 'Constant';

  const showRequiredWarning = field.required && bindingIsEmpty(binding);

  const typeLabel = formatTypeLabel(field.type, field.isCollection);
  const typeHint = getTypeHint(field.type);
  const fieldHint = getFieldHint(field.name);
  const combinedHint = [fieldHint, typeHint].filter(Boolean).join('\n\n');

  const handleKindChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setKind(event.target.value as BindingKind);
    },
    [setKind],
  );

  const handleConstantChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setConstant(event.target.value);
    },
    [setConstant],
  );

  const handleProcessingJobInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setProcessingJobInputTarget(event.target.value);
    },
    [setProcessingJobInputTarget],
  );

  const handleExpressionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setExpression(event.target.value);
    },
    [setExpression],
  );

  const stepOutputBinding = binding && binding.kind === 'StepOutput' ? binding : undefined;
  const currentStepId = stepOutputBinding?.stepId ?? '';
  const currentTarget = stepOutputBinding?.target ?? '';
  const outputOptions = useMemo(
    () => resolveStepOutputs(currentStepId),
    [resolveStepOutputs, currentStepId],
  );

  const handleStepOutputStepChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setStepOutputRef(event.target.value, currentTarget);
    },
    [setStepOutputRef, currentTarget],
  );

  const handleStepOutputTargetSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setStepOutputRef(currentStepId, event.target.value);
    },
    [setStepOutputRef, currentStepId],
  );

  const handleStepOutputTargetInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setStepOutputRef(currentStepId, event.target.value);
    },
    [setStepOutputRef, currentStepId],
  );

  const datalistId = `pji-${field.name}`;

  const renderBindingControls = () => {
    if (effectiveKind === 'Constant') {
      const value = binding && binding.kind === 'Constant' ? toStringValue(binding.value) : '';
      return (
        <StyledFullWidthInput
          type='text'
          value={value}
          onChange={handleConstantChange}
          aria-label={`${field.name} value`}
        />
      );
    }

    if (effectiveKind === 'ProcessingJobInput') {
      const value = binding && binding.kind === 'ProcessingJobInput' ? binding.target : '';
      return (
        <>
          <StyledFullWidthInput
            list={datalistId}
            type='text'
            value={value}
            placeholder='Job input name (e.g. FileId)'
            onChange={handleProcessingJobInputChange}
            aria-label={`${field.name} processing job input`}
          />
          <datalist id={datalistId}>
            {COMMON_PROCESSING_JOB_INPUTS.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </>
      );
    }

    if (effectiveKind === 'Expression') {
      const value = binding && binding.kind === 'Expression' ? binding.expression : '';
      return (
        <StyledExpressionWrapper>
          <ExpressionHelp />
          <StyledExpressionTextarea
            value={value}
            rows={3}
            onChange={handleExpressionChange}
            aria-label={`${field.name} expression`}
          />
        </StyledExpressionWrapper>
      );
    }

    // StepOutput
    return (
      <StyledStepOutputColumn>
        <StyledSelect
          value={currentStepId}
          onChange={handleStepOutputStepChange}
          aria-label={`${field.name} source step`}
        >
          <option value=''>Select step…</option>
          {availableStepIds.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </StyledSelect>
        {outputOptions.length > 0 ? (
          <StyledSelect
            value={currentTarget}
            onChange={handleStepOutputTargetSelectChange}
            aria-label={`${field.name} source output`}
          >
            <option value=''>Select output…</option>
            {outputOptions.map((name) => (
              <option key={name} value={name}>
                {formatName(name, nameMode)}
              </option>
            ))}
          </StyledSelect>
        ) : (
          <StyledFullWidthInput
            type='text'
            placeholder='Output name'
            value={currentTarget}
            onChange={handleStepOutputTargetInputChange}
            aria-label={`${field.name} source output`}
          />
        )}
      </StyledStepOutputColumn>
    );
  };

  return (
    <StyledRow>
      <StyledHeader>
        <StyledLabel>
          <span>{formatName(field.name, nameMode)}</span>
          {typeLabel && <StyledTypeBadge title={field.type}>({typeLabel})</StyledTypeBadge>}
          {field.required && <StyledRequiredMark title='Required field'>*</StyledRequiredMark>}
          {showRequiredWarning && (
            <StyledRequiredWarning
              title='This required field is empty'
              aria-label='Required field is empty'
            >
              &#9888;
            </StyledRequiredWarning>
          )}
          {combinedHint && <InfoHint text={combinedHint} title={field.name} />}
        </StyledLabel>
        <select
          value={kind}
          onChange={handleKindChange}
          title='Where does this value come from?'
          aria-label={`${field.name} binding kind`}
        >
          {allowedSources.map((source) => (
            <option key={source} value={source}>
              {source}
            </option>
          ))}
        </select>
      </StyledHeader>
      {renderBindingControls()}
      {onRemove && (
        <StyledRemoveButton
          type='button'
          onClick={handleRemove}
          className='btn-ghost btn-sm'
          title='Remove this input'
          aria-label={`Remove ${field.name}`}
        >
          &times;
        </StyledRemoveButton>
      )}
    </StyledRow>
  );
};

export default React.memo(InputFieldRenderer);
